import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, useParams } from "react-router-dom";
import Navbar from "../Navbar/Navbar";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Button,
  CircularProgress,
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import KeyData from "./KeyData";
import Events from "./Events";
import Links from "./Links";
import CustomerAssessment from "./CustomerAssessment";
import Contacts from "./Contacts";
import { getCustomer } from "../Search/Network_Calls";
import { postDossierDetails, getDossierDetails } from "../Dossier/Network_call";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Dossier = () => {
  const { implId, id, journey_type } = useParams();
  const [searchParams] = useSearchParams();
  const [filteredData, setFilteredData] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [events, setEvents] = useState([]);
  const [links, setLinks] = useState([]);
  const [customerAssessment, setCustomerAssessment] = useState([]);
  const [keyDataFields, setKeyDataFields] = useState([]);
  const [dossierData, setDossierData] = useState([]);
  const [err, setErr] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSubmittedData, setLastSubmittedData] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  const name = searchParams.get("name") || "Unknown User";
  let lastUpdatedBy = "--";
  let lastUpdatedDate = "--";

  if (dossierData.updatedHistory && dossierData.updatedHistory.length > 0) {
    const mostRecentUpdate = dossierData.updatedHistory.reduce(
      (latest, record) => {
        const recordDate = new Date(record.updated_at);
        const latestDate = new Date(latest.updated_at);
        return recordDate > latestDate ? record : latest;
      }
    );
    lastUpdatedBy = mostRecentUpdate.user_name;
    const updatedDate = new Date(mostRecentUpdate.updated_at);
    lastUpdatedDate = updatedDate.toLocaleDateString();
  }

  const fetchCustomerData = useCallback(async () => {
    const result = await getCustomer(implId, id, journey_type);
    if (result.status === 200) {
      const { users } = result.data;
      setFilteredData(users);
    } else {
      setErr(true);
    }
  }, [implId, id, journey_type]);

  const fetchDossierData = useCallback(async () => {
    const result = await getDossierDetails(implId, id);
    if (result.status === 200) {
      let data = result.data;
      setDossierData(data);
      setIsLoading(false);
    } else {
      setErr(true);
      setIsLoading(false);
    }
  }, [implId, id]);

  useEffect(() => {
    fetchCustomerData();
    fetchDossierData();
  }, [fetchCustomerData, fetchDossierData]);

  const handleKeyDataChange = useCallback((newKeyDataFields) => {
    setKeyDataFields(newKeyDataFields);
  }, []);

  const handleContactsChange = useCallback((newContacts) => {
    setContacts(newContacts);
  }, []);

  const handleEventChange = useCallback((newEvents) => {
    setEvents(newEvents);
  }, []);

  const handleLinkChange = useCallback((newLinks) => {
    setLinks(newLinks);
  }, []);

  const handleCustomerAssessmentChange = useCallback(
    (newCustomerAssessment) => {
      setCustomerAssessment(newCustomerAssessment);
    },
    []
  );

 
  useEffect(() => {
    const keyDataObject = keyDataFields.reduce(
      (acc, { label, value, specificDate, id }) => {
        if (
          [
            "firstInteractionDate",
            "recentInteractionDate",
            "becameCustomer",
            "firstPurchase",
            "recentPurchase",
          ].includes(id)
        ) {
          acc[label] = specificDate !== "" ? specificDate : null;
        } else {
          acc[label] = value !== "--" ? value : null;
        }
        return acc;
      },
      {}
    );

    const currentData = {
      contacts,
      events,
      links,
      customerAssessment,
      keyDataFields: keyDataObject,
    };

    const hasDataChanges = lastSubmittedData
      ? JSON.stringify(currentData) !== JSON.stringify(lastSubmittedData)
      : contacts.length > 0 ||
        events.length > 0 ||
        links.length > 0 ||
        customerAssessment.length > 0 ||
        keyDataFields.some(
          (field) =>
            field.value !== "--" ||
            (field.specificDate && field.specificDate !== "")
        );

    setHasChanges(hasDataChanges);
  }, [
    contacts,
    events,
    links,
    customerAssessment,
    keyDataFields,
    lastSubmittedData,
  ]);

  const handleSubmit = async () => {
    if (isSaving || !hasChanges) return;

    setIsSaving(true);

    const keyDataObject = keyDataFields.reduce(
      (acc, { label, value, specificDate, id }) => {
        if (
          [
            "firstInteractionDate",
            "recentInteractionDate",
            "becameCustomer",
            "firstPurchase",
            "recentPurchase",
          ].includes(id)
        ) {
          acc[label] = specificDate !== "" ? specificDate : null;
        } else {
          acc[label] = value !== "--" ? value : null;
        }
        return acc;
      },
      {}
    );

    const dossierDetails = {
      contacts,
      events,
      links,
      customerAssessment,
      keyDataFields: keyDataObject,
    };

    try {
      const result = await postDossierDetails(implId, id, dossierDetails);
      if (result.status === 200) {
        setLastSubmittedData(dossierDetails);
        await fetchDossierData();
        toast.success("Dossier details submitted successfully");
      } else {
        toast.error("Failed to submit dossier details");
      }
    } catch (error) {
      toast.error("An error occurred while submitting the dossier details");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          marginTop: "2rem",
        }}
      >
        <Typography
          variant="h6"
          component="div"
          gutterBottom
          sx={{ display: "flex", justifyItems: "center", alignItems: "center" }}
        >
          <CircularProgress sx={{ color: "#1FAFC2", marginLeft: "8px" }} />
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Navbar />
      <Box
        sx={{
          padding: "20px",
          margin: "0 auto",
          maxWidth: "900px",
          position: "relative",
        }}
      >
        <div>
          <Card
            sx={{
              maxWidth: "100%",
              margin: "auto",
              marginTop: "20px",
              textAlign: "center",
            }}
          >
            <CardContent>
              <Typography
                variant="h4"
                component="div"
                gutterBottom
                sx={{ fontWeight: "bold", color: "#00A0B2" }}
              >
                {decodeURIComponent(name)}
              </Typography>
              <Grid
                container
                justifyContent="center"
                alignItems="center"
                spacing={1}
              >
                <Grid item>
                  <Typography variant="body2" component="div">
                    <span style={{ color: "#FF762B" }}>Last Updated by:</span>{" "}
                    {lastUpdatedBy}
                  </Typography>
                </Grid>
                <Grid item sx={{ color: "#FF762B" }}>
                  <CalendarTodayIcon fontSize="small" />
                </Grid>
                <Grid item>
                  <Typography variant="body2" component="div">
                    <span style={{ color: "#FF762B" }}>Last Updated Date:</span>{" "}
                    {lastUpdatedDate}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
          <Box sx={{ marginTop: "20px" }}>
            <Grid>
              <KeyData
                filteredData={filteredData}
                onDataChange={handleKeyDataChange}
                dossierData={dossierData}
                refreshDossierData={fetchDossierData}
              />
            </Grid>
          </Box>
          <Box sx={{ marginTop: "20px" }}>
            <Grid>
              <Contacts
                onContactsChange={handleContactsChange}
                dossierData={dossierData}
                refreshDossierData={fetchDossierData}
              />
            </Grid>
          </Box>
          <Box sx={{ marginTop: "20px" }}>
            <Grid>
              <Events
                onEventChange={handleEventChange}
                dossierData={dossierData}
                refreshDossierData={fetchDossierData}
              />
            </Grid>
          </Box>
          <Box sx={{ marginTop: "20px" }}>
            <Grid>
              <Links
                onLinksChange={handleLinkChange}
                dossierData={dossierData}
                refreshDossierData={fetchDossierData}
              />
            </Grid>
          </Box>
          <Box sx={{ marginTop: "20px" }}>
            <Grid>
              <CustomerAssessment
                onCustomerAssessmentChange={handleCustomerAssessmentChange}
                dossierData={dossierData}
              />
            </Grid>
          </Box>
        </div>
        <Box
          sx={{
            position: "fixed",
            top: "50%",
            right: "20px",
            transform: "translateY(-50%)",
            zIndex: 1000,
          }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={isSaving || !hasChanges}
            sx={{
              minWidth: 120,
              backgroundColor: hasChanges ? "#00A0B2" : "#cccccc",
              "&:hover": {
                backgroundColor: hasChanges ? "#008B9A" : "#cccccc",
              },
            }}
          >
            {isSaving ? <CircularProgress size={24} /> : "Save"}
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default Dossier;
